if (!customElements.get('product-v2-sticky-atc')) {
  customElements.define(
    'product-v2-sticky-atc',
    class ProductV2StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onMobileMediaChange = this.onMobileMediaChange.bind(this);
        this.intersectionObserver = null;
        this.loadingObserver = null;
        this.resizeObserver = null;
        this.isBarVisible = false;
        this.isSectionVisible = true;
      }

      connectedCallback() {
        this.productV2 = this.closest('product-v2');
        this.button = this.querySelector('.product-v2-sticky-atc__button');
        this.buttonText = this.button?.querySelector('span');
        this.spinner = this.button?.querySelector('.loading__spinner');
        this.sectionElement = this.productV2?.closest('.shopify-section');
        this.variantPickerBlock = this.productV2?.querySelector('.product-v2__block--variant-picker');
        this.buyButtonsBlock = this.productV2?.querySelector('.product-v2__block--buy-buttons');
        this.scrollToProduct = this.dataset.scrollToProduct === 'true';
        this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');

        this.button?.addEventListener('click', this.onClick);
        this.setupVisibilityObserver();
        this.setupLoadingSync();
        this.setupResizeObserver();

        if (this.productV2?.syncBuyRowState) {
          this.productV2.syncBuyRowState(this.productV2.findMatchingVariant());
        }
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClick);
        this.mobileMediaQuery?.removeEventListener('change', this.onMobileMediaChange);
        this.intersectionObserver?.disconnect();
        this.loadingObserver?.disconnect();
        this.resizeObserver?.disconnect();
        this.setBodyPadding(false);
      }

      setupVisibilityObserver() {
        if (!this.sectionElement) return;

        this.mobileMediaQuery.addEventListener('change', this.onMobileMediaChange);

        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            this.isSectionVisible = entry.isIntersecting;
            this.updateVisibility();
          },
          { threshold: 0 }
        );

        this.intersectionObserver.observe(this.sectionElement);
      }

      onMobileMediaChange() {
        this.updateVisibility();
      }

      updateVisibility() {
        const shouldShow = this.mobileMediaQuery.matches && !this.isSectionVisible;

        this.classList.toggle('is-visible', shouldShow);
        this.toggleAttribute('hidden', !shouldShow);
        this.setBodyPadding(shouldShow);
      }

      setupResizeObserver() {
        if (!window.ResizeObserver) return;

        this.resizeObserver = new ResizeObserver(() => {
          if (this.isBarVisible) {
            this.updateBodyPaddingHeight();
          }
        });

        this.resizeObserver.observe(this);
      }

      updateBodyPaddingHeight() {
        document.body.style.setProperty('--product-v2-sticky-atc-height', `${this.offsetHeight}px`);
      }

      setBodyPadding(isVisible) {
        this.isBarVisible = isVisible;

        if (!isVisible) {
          document.body.classList.remove('has-product-v2-sticky-atc-visible');
          document.body.style.removeProperty('--product-v2-sticky-atc-height');
          return;
        }

        document.body.classList.add('has-product-v2-sticky-atc-visible');
        this.updateBodyPaddingHeight();
      }

      setupLoadingSync() {
        if (this.scrollToProduct) return;

        const mainSubmit = this.getMainSubmitButton();
        if (!mainSubmit || !this.button) return;

        this.loadingObserver = new MutationObserver(() => {
          const isLoading = mainSubmit.classList.contains('loading');
          this.button.classList.toggle('loading', isLoading);
          this.spinner?.classList.toggle('hidden', !isLoading);
        });

        this.loadingObserver.observe(mainSubmit, { attributes: true, attributeFilter: ['class'] });
      }

      getMainSubmitButton() {
        const sectionId = this.productV2?.sectionId;
        if (!sectionId) return null;
        return this.productV2.querySelector(`#ProductSubmitButton-${sectionId}`);
      }

      scrollToTarget(element) {
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      onClick() {
        if (this.button?.classList.contains('loading')) return;

        if (this.button?.dataset.action === 'scroll-to-picker') {
          this.scrollToTarget(this.variantPickerBlock);
          return;
        }

        if (this.button?.dataset.action === 'scroll-to-product') {
          this.scrollToTarget(this.buyButtonsBlock || this.variantPickerBlock);
          return;
        }

        if (this.button?.disabled) return;

        const mainSubmit = this.getMainSubmitButton();
        if (!mainSubmit || mainSubmit.disabled) return;

        mainSubmit.click();
      }

      syncState({ hasAllOptionsSelected, resolvedVariant, isAvailable, selectSizeText }) {
        if (!this.button || !this.buttonText) return;

        const selectSize = selectSizeText || this.dataset.selectSizeText || 'SELECT A SIZE';

        if (!hasAllOptionsSelected) {
          this.button.disabled = false;
          this.button.dataset.action = 'scroll-to-picker';
          this.buttonText.textContent = selectSize;
          this.button.setAttribute('aria-label', selectSize);
          return;
        }

        delete this.button.dataset.action;

        if (!resolvedVariant?.id) {
          const unavailableText = window.variantStrings?.unavailable || 'Unavailable';
          this.button.disabled = true;
          this.buttonText.textContent = unavailableText;
          this.button.setAttribute('aria-label', unavailableText);
          return;
        }

        if (isAvailable) {
          const addToCartText = window.variantStrings?.addToCart || 'Add to cart';
          this.button.disabled = false;
          this.buttonText.textContent = addToCartText;
          this.button.setAttribute('aria-label', addToCartText);

          if (this.scrollToProduct) {
            this.button.dataset.action = 'scroll-to-product';
          }
        } else {
          const soldOutText = window.variantStrings?.soldOut || 'Sold out';
          this.button.disabled = true;
          this.buttonText.textContent = soldOutText;
          this.button.setAttribute('aria-label', soldOutText);
        }
      }
    }
  );
}
